/**
 * Custom Pagination Web Component
 */

(() => {
  if (!customElements.get("custom-pagination")) {
    class CustomPagination extends HTMLElement {
      constructor() {
        super();
        this.currentPage = 2;
        this.isLoading = false;
        this.hasNextPage = true;
        this.sectionId = null;
        this.productGrid = null;
        this.loadMoreButton = null;
      }

      connectedCallback() {
        this.init();
      }

      disconnectedCallback() {
        if (this.loadMoreButton) {
          this.loadMoreButton.removeEventListener("click", this.handleLoadMore);
        }
      }

      init() {
        this.setupElements();
        this.setupEventListeners();
      }

      setupElements() {
        this.productGrid = document.querySelector("#product-grid");

        if (this.productGrid) {
          this.sectionId = this.productGrid.dataset.id;
        }

        this.loadMoreButton = this.querySelector(".load-more, [data-load-more-button]");

        if (!this.productGrid || !this.sectionId) {
          console.warn("Custom Pagination: Product grid or section ID not found");
          return;
        }
      }

      setupEventListeners() {
        if (this.loadMoreButton) {
          this.handleLoadMore = this.handleLoadMore.bind(this);
          this.loadMoreButton.addEventListener("click", this.handleLoadMore);
        }
      }

      handleLoadMore(event) {
        event.preventDefault();
        event.stopPropagation();

        if (this.isLoading || !this.hasNextPage) {
          return;
        }

        this.loadProducts();

        if (event.target) {
          event.target.blur();
        }
      }

      buildSectionUrl() {
        const currentUrl = new URL(window.location);
        currentUrl.searchParams.set("section_id", this.sectionId);
        currentUrl.searchParams.set("page", this.currentPage);

        return currentUrl.pathname + currentUrl.search;
      }

      async loadProducts() {
        if (this.isLoading || !this.hasNextPage) {
          return;
        }

        this.setLoadingState(true);

        try {
          const url = this.buildSectionUrl();
          const response = await fetch(url);

          if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
          }

          const responseText = await response.text();
          this.renderProducts(responseText);

          this.currentPage++;

          // Dispatch event for tracking
          this.dispatchEvent(
            new CustomEvent("pagination:page-loaded", {
              detail: {
                page: this.currentPage - 1,
                url: url,
              },
              bubbles: true,
            })
          );
        } catch (error) {
          console.error("Custom Pagination: Error loading products", error);
        } finally {
          this.setLoadingState(false);
        }
      }

      renderProducts(html) {
        const parser = new DOMParser();
        const doc = parser.parseFromString(html, "text/html");

        const newProductGrid = doc.querySelector("#product-grid");

        if (!newProductGrid) {
          this.hasNextPage = false;
          this.hideLoadMoreButton();
          return;
        }

        const newProducts = Array.from(newProductGrid.children);

        if (newProducts.length === 0) {
          this.hasNextPage = false;
          this.hideLoadMoreButton();
          return;
        }

        const paginationInResponse = doc.querySelector("custom-pagination");
        if (!paginationInResponse) {
          this.hasNextPage = false;
        }

        newProducts.forEach((product) => {
          this.productGrid.appendChild(product);
        });

        if (!this.hasNextPage) {
          this.hideLoadMoreButton();
        }

        window.dispatchEvent(
          new CustomEvent("product-grid:updated", {
            detail: { productGrid: this.productGrid },
          })
        );
      }

      setLoadingState(loading) {
        this.isLoading = loading;

        if (this.loadMoreButton) {
          this.loadMoreButton.disabled = loading;
          this.loadMoreButton.classList.toggle("loading", loading);

          const buttonText = this.loadMoreButton.querySelector("span");

          if (loading) {
            this.loadMoreButton.setAttribute("aria-label", "Loading...");
          } else {
            const originalText = buttonText ? buttonText.textContent : "Load more";
            this.loadMoreButton.setAttribute("aria-label", originalText);
          }
        }

        this.classList.toggle("loading", loading);
      }

      hideLoadMoreButton() {
        if (this.loadMoreButton) {
          this.loadMoreButton.classList.add("hidden");
        }
      }

      loadMore() {
        this.loadProducts();
      }

      reset() {
        this.currentPage = 2;
        this.hasNextPage = true;
        this.isLoading = false;

        if (this.loadMoreButton) {
          this.loadMoreButton.classList.remove("hidden");
          this.loadMoreButton.disabled = false;
        }
      }
    }

    customElements.define("custom-pagination", CustomPagination);
  }
})();
